import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { resetPassword } from '../../Services/APIs/authentication';
import useFlowState from './useFlowState';
import AuthLayout from './AuthLayout';

const errorClasses = "text-xs text-cv2-destructive mt-1";

const NewPassword = () => {
    const navigate = useNavigate();
    const { email, otp, clear } = useFlowState('clinicPasswordReset', ['email', 'otp'], '/forgot-password');

    const [submitError, setSubmitError] = useState('');
    const [submitSuccess, setSubmitSuccess] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const initialValues = { password: '', confirmPassword: '' };

    const validationSchema = Yup.object().shape({
        password: Yup.string()
            .min(6, 'Password must be between 6 and 72 characters')
            .max(72, 'Password must be between 6 and 72 characters')
            .required('Password is required'),
        confirmPassword: Yup.string()
            .oneOf([Yup.ref('password')], 'Passwords must match')
            .required('Confirm password is required'),
    });

    const handleSubmit = async (values, { setSubmitting }) => {
        setSubmitError('');
        setSubmitSuccess('');
        try {
            const response = await resetPassword({
                authenticate_email: email,
                otp,
                password: values.password,
                confirm_password: values.confirmPassword,
            });
            setSubmitSuccess(response.data?.message || 'Password reset successfully. Please log in with your new password.');
            clear();
            navigate('/login', { replace: true });
        } catch (error) {
            setSubmitError(error?.response?.data?.message || error?.data?.message || 'Something went wrong. Please try again.');
        }
        setSubmitting(false);
    };

    return (
        <AuthLayout variant="signin">

                <div className="flex-1 flex flex-col justify-center">
                    <div className="mb-12">
                        <p className="cv2-auth-eyebrow font-spaceMono text-[9px] tracking-[1.4px] uppercase mb-3.5">
                            Account Recovery
                        </p>
                        <h1 className="font-aeonik font-normal text-[38px] leading-[44px] tracking-[-0.9px] text-cv2-foreground normal-case mb-3">
                            Set New<br />Password
                        </h1>
                        <p className="cv2-auth-subcopy-strong font-aeonik text-sm normal-case">
                            Choose a new password for <span className="text-cv2-foreground">{email || 'your account'}</span>
                        </p>
                    </div>

                    {submitSuccess && (
                        <div className="text-sm text-green-500 bg-green-500/10 border border-green-500/30 rounded-md px-3 py-2 mb-4">
                            {submitSuccess}
                        </div>
                    )}
                    {submitError && (
                        <div className="cv2-auth-error-banner text-sm border rounded-md px-3 py-2 mb-4">
                            {submitError}
                        </div>
                    )}

                    <Formik
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                    >
                        {({ isSubmitting }) => (
                            <Form className="flex flex-col">
                                <div className="cv2-auth-row border-t py-4">
                                    <label htmlFor="password" className="cv2-auth-label block font-spaceMono text-[9px] tracking-[1.2px] uppercase mb-[7px]">
                                        New Password
                                    </label>
                                    <div className="flex items-center gap-3">
                                        <Field
                                            type={showPassword ? 'text' : 'password'}
                                            name="password"
                                            placeholder="••••••••"
                                            className="cv2-auth-input flex-1 bg-transparent border-0 outline-none font-aeonik text-[15px] text-cv2-foreground normal-case"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword((prev) => !prev)}
                                            className="cv2-auth-toggle shrink-0 text-cv2-foreground"
                                        >
                                            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                        </button>
                                    </div>
                                    <ErrorMessage name="password" component="div" className={errorClasses} />
                                </div>

                                <div className="cv2-auth-row border-t py-4">
                                    <label htmlFor="confirmPassword" className="cv2-auth-label block font-spaceMono text-[9px] tracking-[1.2px] uppercase mb-[7px]">
                                        Confirm Password
                                    </label>
                                    <div className="flex items-center gap-3">
                                        <Field
                                            type={showConfirmPassword ? 'text' : 'password'}
                                            name="confirmPassword"
                                            placeholder="••••••••"
                                            className="cv2-auth-input flex-1 bg-transparent border-0 outline-none font-aeonik text-[15px] text-cv2-foreground normal-case"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowConfirmPassword((prev) => !prev)}
                                            className="cv2-auth-toggle shrink-0 text-cv2-foreground"
                                        >
                                            {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                        </button>
                                    </div>
                                    <ErrorMessage name="confirmPassword" component="div" className={errorClasses} />
                                </div>

                                <div className="cv2-auth-row border-t" />

                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="mt-7 w-full h-12 bg-claraview-secondary hover:brightness-110 disabled:opacity-50 transition-all rounded-[3px] font-spaceMono text-[11px] tracking-[1.2px] text-claraview-quaternary uppercase"
                                >
                                    Reset Password
                                </button>
                            </Form>
                        )}
                    </Formik>
                </div>

                <Link to="/login" className="cv2-auth-footer mt-10 text-center font-aeonik text-[13px] normal-case block">
                    ← Back to Login
                </Link>
        </AuthLayout>
    );
};

export default NewPassword;
